import { LoadingSpinner } from "components/LoadingSpinner";
import { orderTypeConstants } from "constants/order";
import { UserRole } from "constants/userRole";
import { useUserWithAccount } from "context/UserAccountProvider";
import { parsePrice, centsToDollars, dollarsToCents } from "helpers/price";
import { useFinixPaymentProvider } from "hooks/useFinixPaymentProvider";
import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  IRefundableCard,
  useGetRefundableCardsQuery,
} from "store/queries/moneyTransfers";
import { userSelector } from "store/slices/user";
import styled from "styled-components";
import { IOrder } from "types/Order/Order";

interface ITransfer {
  selectedPartyToTransferFrom: {
    name: string;
    id: string;
    totalAmount: number;
    type: string;
  } | null;
  selectedPartyToTransferTo: {
    name: string;
    id: string;
    totalAmount: number;
    type: string;
  } | null;
  selectedAmountToTransfer: number;
  withholdClearFee?: boolean;
  // CLE-2458: chosen source Payment.id when refunding a split (multi-card) payment to the patient.
  selectedRefundCardPaymentId?: string;
}
export const StyledButton = styled(Button)`
  gap: ${sizer(2)};
  margin-bottom: ${sizer(4)};

  span:first-child {
    font-size: ${sizer(3)};
  }
`;

export function SingleTransferForm({
  order,
  transfer,
  transfers,
  setTransfers,
  index,
  handleRemoveTransfer,
  showRemoveButton,
}: {
  order: IOrder;
  transfer: ITransfer;
  transfers: ITransfer[];
  setTransfers: any;
  index: number;
  showRemoveButton: boolean;
  handleRemoveTransfer: (index: number) => void;
}) {
  const { accounts } = useUserWithAccount();
  const user = useSelector(userSelector);
  const isSuperAdmin = user.scope === UserRole.SUPER_ADMIN;
  // Strict-freeze: the money-transfer form's rail-specific behavior follows the
  // rail this order is BOUND to (authoritative endpoint via the hook), not the
  // account's current useFinix flag. Error-fallback policy: this form moves real
  // money, so when the rail can't be determined (endpoint loading OR errored) we
  // FAIL CLOSED rather than guess from the account flag — see the gate below.
  const {
    isFinix: useFinix,
    isLoading: isProviderLoading,
    isError: isProviderError,
  } = useFinixPaymentProvider(order);

  // CLE-2458: distinct cards the patient used to pay. Returns 2+ only for split (multi-card)
  // payments, so the "Refund to card" picker appears only when there's actually a choice.
  const { data: refundableCardsResponse } = useGetRefundableCardsQuery(
    order.id
  );
  const refundableCards = refundableCardsResponse?.data || [];
  const toIsPatient = transfer.selectedPartyToTransferTo?.type === "patient";
  const showRefundCardPicker = toIsPatient && refundableCards.length >= 2;
  // CLE-2634: how much a card can still take back comes from the server, which reads it from
  // Stripe. This used to be derived here by subtracting money-transfer items that carried a
  // sourcePaymentId — which made every refund NOT pinned to a card (auto-selected, pre-CLE-2458,
  // or issued straight from the Stripe dashboard) invisible, so the form happily offered a cap the
  // card had no room for and the transfer failed after the fact. Null on Finix, where the binding
  // limit is the provider∩card slice computed below instead.
  const getCardRemainingInCents = (card?: IRefundableCard) =>
    card?.remainingRefundableInCents ?? undefined;
  // CLE-2458: a refund pinned to a card can't exceed what that card can still take back, so this is
  // the upper bound for the amount field while a specific card is selected.
  const selectedRefundCard = refundableCards.find(
    (card) => card.paymentId === transfer.selectedRefundCardPaymentId
  );
  // CLE-2458: cards already pinned by OTHER transfers in this batch. Each card can only absorb its
  // own charges, so two transfers must not both target the same card — disable those options here.
  const cardsChosenByOtherTransfers = transfers
    .filter((_, i) => i !== index)
    .map((t) => t.selectedRefundCardPaymentId)
    .filter(Boolean);
  // CLE-2458: a patient refund reverses the chosen provider's transfers, and a split-paid card's
  // money is spread across providers. So the real cap for "refund $X to card C from provider P" is
  // how much of card C's money provider P still holds — NOT card C's full charge. We compute that
  // (provider ∩ card) reversible from the order's provider transfers; reversal rows are negative and
  // carry the same providerId+paymentId, so summing nets out anything already refunded. The backend
  // enforces the same limit before any reversal, so this is a UX guard, not the source of truth.
  const getProviderCardReversibleInCents = (
    providerId?: string | null,
    cardPaymentId?: string
  ): number | undefined => {
    if (!providerId || !cardPaymentId) return undefined;
    const finixRows: any[] = (order.providerFinixTransfers || []).filter(
      (t: any) => t.providerId === providerId && t.paymentId === cardPaymentId
    );
    const stripeRows: any[] = (order.providerStripeTransfers || []).filter(
      (t: any) => t.providerId === providerId && t.paymentId === cardPaymentId
    );
    const rows: any[] = finixRows.length ? finixRows : stripeRows;
    const net = rows.reduce(
      (sum: number, t: any) =>
        sum + t.amountInCents + (t.clearFeeAmountInCents || 0),
      0
    );
    return Math.max(0, net);
  };
  // The (provider ∩ card) limit only binds on FINIX, where a patient refund REVERSES the specific
  // provider transfer the card funded — so the card can only get back what that provider held.
  // On STRIPE the refund hits the card's original charge directly; "how much of each card went to
  // each provider" is only our local ProviderStripeTransfer bookkeeping, not something Stripe
  // enforces (patient→ClearHealth and ClearHealth→provider are separate transfers). So a Stripe card
  // refund is bounded by the card's full charge (plus the provider's total reversible), not the slice.
  const fromIsClearHealth =
    !!transfer.selectedPartyToTransferFrom &&
    transfer.selectedPartyToTransferFrom.id === null;
  const providerCardReversibleInCents = getProviderCardReversibleInCents(
    transfer.selectedPartyToTransferFrom?.id,
    selectedRefundCard?.paymentId
  );
  // The per-provider slice is the binding card limit only on Finix when refunding from a provider.
  const usesProviderCardCap =
    useFinix &&
    !fromIsClearHealth &&
    !!transfer.selectedPartyToTransferFrom?.id;
  // The max refundable to the chosen card given the current "From" party and rail. On Finix from a
  // provider it's the (provider ∩ card) reversible (already net of reversals); otherwise it's the
  // remaining refundable the server read from Stripe for that card's charges.
  const selectedCardCapInCents = (() => {
    if (!selectedRefundCard) return undefined;
    if (usesProviderCardCap) return providerCardReversibleInCents;
    return getCardRemainingInCents(selectedRefundCard);
  })();
  // Effective max for the amount field: the smaller of the provider's total reversible amount and
  // (when a card is pinned) the max refundable to that card.
  const amountMaxInCents = (() => {
    const providerCap = transfer.selectedPartyToTransferFrom?.id
      ? transfer.selectedPartyToTransferFrom.totalAmount
      : undefined;
    const caps = [providerCap, selectedCardCapInCents].filter(
      (value): value is number => value != null
    );
    return caps.length ? Math.min(...caps) : undefined;
  })();

  const paymentBreakdownDetails = useMemo(() => {
    return order?.paymentFullBreakdown?.bundledProviderAmounts;
  }, [order]);
  const paymentPlanPayments = useMemo(() => {
    return order?.paymentPlanBreakdown?.payments;
  }, [order]);
  const providerStripeTransfers = useMemo(() => {
    return order?.providerStripeTransfers;
  }, [order]);

  const providerFinixTransfers = useMemo(() => {
    return order?.providerFinixTransfers;
  }, [order]);

  const providerTotals = useMemo(() => {
    const totals: {
      [key: string]: {
        name: string;
        id: string;
        totalAmount: number;
        type: string;
      };
    } = {};
    // Aggregate from Stripe transfers
    providerStripeTransfers?.forEach((transfer) => {
      if (!totals[transfer.providerId]) {
        totals[transfer.providerId] = {
          name: transfer.provider.name,
          id: transfer.providerId,
          totalAmount: 0,
          type: "provider",
        };
      }
      totals[transfer.providerId].totalAmount +=
        transfer.amountInCents + transfer.clearFeeAmountInCents;
    });
    // Aggregate from Finix transfers. Skip CLEARHEALTH_UPCHARGE rows — those have a
    // null providerId/provider and represent the Clear Health merchant's split, not a
    // refundable provider pot. Backend mirrors this in getFinixTransfersStateByProviderId.
    providerFinixTransfers?.forEach((transfer: any) => {
      if (!transfer.providerId || !transfer.provider) return;
      if (!totals[transfer.providerId]) {
        totals[transfer.providerId] = {
          name: transfer.provider.name,
          id: transfer.providerId,
          totalAmount: 0,
          type: "provider",
        };
      }
      totals[transfer.providerId].totalAmount +=
        transfer.amountInCents + (transfer.clearFeeAmountInCents || 0);
    });
    return Object.values(totals);
  }, [providerStripeTransfers, providerFinixTransfers]);

  const eligibleToProviders = useMemo(() => {
    const providersInOrder: {
      [key: string]: { name: string; id: string; type: string };
    } = {};
    const otherProvidersInAccount: {
      [key: string]: { name: string; id: string; type: string };
    } = {};
    if (paymentPlanPayments) {
      // for payment plans, only allow refunds of payments that have already been transferred to providers
      paymentPlanPayments?.forEach((payment) => {
        if (payment.transferredToProviders) {
          payment.providerBreakdown.forEach((amount: any) => {
            if (!providersInOrder[amount.providerId]) {
              providersInOrder[amount.providerId] = {
                name: amount.providerName,
                id: amount.providerId,
                type: "provider",
              };
            }
          });
        }
      });
    } else {
      // no payment plan
      paymentBreakdownDetails?.forEach((amount) => {
        if (!providersInOrder[amount.providerId]) {
          providersInOrder[amount.providerId] = {
            name: amount.providerName,
            id: amount.providerId,
            type: "provider",
          };
        }
      });
    }
    const accountWithProviders = accounts?.data?.find(
      (account) => account.id === order.accountId
    );
    if (accountWithProviders?.providers) {
      [...accountWithProviders.providers]
        .filter(
          (provider) =>
            !!provider.stripeAccountNumber || !!provider.finixMerchantId
        )
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((provider) => {
          if (!otherProvidersInAccount[provider.id]) {
            otherProvidersInAccount[provider.id] = {
              name: provider.name,
              id: provider.id,
              type: "provider",
            };
          }
        });
    }

    return {
      providersInOrder: Object.values(providersInOrder),
      otherProvidersInAccount: Object.values(otherProvidersInAccount),
    };
  }, [accounts, order, paymentPlanPayments, paymentBreakdownDetails]);

  const transferFromOptions = useMemo(() => {
    // Under the Finix APPLICATION_FEE model the ClearHealth merchant is no longer a
    // destination in split_transfers, so it cannot originate a reversal. Hide it from
    // the "from" dropdown entirely on Finix orders.
    const fromOptions = [
      ...providerTotals,
      ...(useFinix
        ? []
        : [
            {
              name: "Clear Health",
              id: null,
              totalAmount: null,
              type: "provider",
            },
          ]),
    ];
    return fromOptions;
  }, [providerTotals, order]);

  const transferToOptions = useMemo(() => {
    // Create grouped options structure with empty array fallbacks
    const providersInOrder = eligibleToProviders?.providersInOrder || [];
    const otherProvidersInAccount =
      eligibleToProviders?.otherProvidersInAccount || [];

    // Check if any payment on the order was made by check
    const hasCheckPayment = order.payments?.some(
      (payment) => payment.type === "Check"
    );

    // CLEAR_TO_PATIENT is disabled for Finix accounts. Under the new APPLICATION_FEE
    // model the ClearHealth merchant is no longer a destination in split_transfers, so a
    // reversal naming ClearHealth fails Finix validation. When the "from" party is Clear
    // Health AND the order is on a Finix rail, hide the patient option in "to".
    const fromIsClearHealth = transfer.selectedPartyToTransferFrom?.id === null;
    const hideClearToPatient = useFinix && fromIsClearHealth;

    // Build patient and Clear Health items, excluding patient if payment was by check
    // OR when CLEAR_TO_PATIENT is disabled (Finix accounts with from=Clear Health)
    const patientAndClearHealthItems = [
      ...(!hasCheckPayment && !hideClearToPatient
        ? [
            {
              name: `${order.patient.firstName} ${order.patient.lastName}`,
              id: order.patientId,
              type: "patient",
            },
          ]
        : []),
      { name: "Clear Health", id: null, type: "provider" },
    ];

    // Everything must be in groups for consistency
    return [
      {
        label: "Patient and Clear Health",
        items: patientAndClearHealthItems,
        type: "group",
      },
      ...(providersInOrder.length > 0
        ? [
            {
              label: "Providers in this estimate",
              items: providersInOrder,
              type: "group",
            },
          ]
        : []),
      ...(otherProvidersInAccount.length > 0
        ? [
            {
              label: "Other providers in account",
              items: otherProvidersInAccount,
              type: "group",
            },
          ]
        : []),
    ];
  }, [order, eligibleToProviders, transfer.selectedPartyToTransferFrom]);

  const itemTemplate = (item: any) => {
    if (item.type === "group") {
      return null;
    }
    return (
      <div>
        {item.name}{" "}
        {item.totalAmount ? parsePrice(centsToDollars(item.totalAmount)) : ""}
      </div>
    );
  };

  const optionGroupTemplate = (option: any) => {
    return (
      <div className="flex align-items-center text-primary">{option.label}</div>
    );
  };

  const valueTemplate = (option: any, props: any) => {
    return option ? (
      <div>
        {option.name}{" "}
        {option.totalAmount
          ? parsePrice(centsToDollars(option.totalAmount))
          : ""}
      </div>
    ) : (
      // eslint-disable-next-line react/prop-types
      <div>{props.placeholder}</div>
    );
  };

  const onTransferFromChange = (selectedParty: any) => {
    const isClearHealth = selectedParty.id === null;
    const totalAllowable = selectedParty?.totalAmount;
    const currentAmount = transfer.selectedAmountToTransfer;
    if (isClearHealth) {
      // If switching from a provider to Clear Health on a Finix rail where the user
      // had previously selected the patient as "to", clear the now-invalid "to" selection.
      const toIsPatient =
        transfer.selectedPartyToTransferTo?.type === "patient";
      const shouldClearTo = useFinix && toIsPatient;
      setTransfers((prevTransfers: any) => {
        const updatedTransfers = [...prevTransfers];
        updatedTransfers[index] = {
          ...updatedTransfers[index],
          selectedPartyToTransferFrom: selectedParty,
          ...(shouldClearTo ? { selectedPartyToTransferTo: null } : {}),
        };
        return updatedTransfers;
      });
      return;
    }
    const updatedAmount =
      currentAmount > totalAllowable ? totalAllowable : currentAmount;
    setTransfers((prevTransfers: any) => {
      const updatedTransfers = [...prevTransfers];
      updatedTransfers[index] = {
        ...updatedTransfers[index],
        selectedPartyToTransferFrom: selectedParty,
        selectedAmountToTransfer: updatedAmount,
      };
      return updatedTransfers;
    });
  };

  const onTransferToChange = (selectedParty: any) => {
    setTransfers((prevTransfers: any) => {
      const updatedTransfers = [...prevTransfers];
      updatedTransfers[index] = {
        ...updatedTransfers[index],
        selectedPartyToTransferTo: selectedParty,
        // CLE-2458: a chosen refund card only applies to a patient refund; clear it otherwise.
        ...(selectedParty?.type === "patient"
          ? {}
          : { selectedRefundCardPaymentId: undefined }),
      };
      return updatedTransfers;
    });
  };

  const onRefundCardChange = (paymentId: string) => {
    // CLE-2458: clamp an already-entered amount down to the max refundable to the newly chosen card.
    // On Finix from a provider that's the provider ∩ card amount; otherwise the card's remaining charge.
    const newCard = refundableCards.find((c) => c.paymentId === paymentId);
    const cap = usesProviderCardCap
      ? getProviderCardReversibleInCents(
          transfer.selectedPartyToTransferFrom?.id,
          paymentId
        )
      : getCardRemainingInCents(newCard);
    setTransfers((prevTransfers: any) => {
      const updatedTransfers = [...prevTransfers];
      const current = updatedTransfers[index];
      const clampedAmount =
        cap != null && current.selectedAmountToTransfer > cap
          ? cap
          : current.selectedAmountToTransfer;
      updatedTransfers[index] = {
        ...current,
        selectedRefundCardPaymentId: paymentId,
        selectedAmountToTransfer: clampedAmount,
      };
      return updatedTransfers;
    });
  };
  const onAmountChange = (amount: number | null) => {
    let newAmount = amount || 0;
    const isClearHealth = transfer?.selectedPartyToTransferFrom?.id === null;
    const totalAllowable = transfer?.selectedPartyToTransferFrom?.totalAmount;
    if (
      !isClearHealth &&
      amount &&
      totalAllowable &&
      dollarsToCents(amount) > totalAllowable
    ) {
      newAmount = centsToDollars(totalAllowable);
    }
    // CLE-2458: never let a card-pinned refund exceed the max refundable to that card.
    if (
      selectedCardCapInCents != null &&
      dollarsToCents(newAmount) > selectedCardCapInCents
    ) {
      newAmount = centsToDollars(selectedCardCapInCents);
    }
    setTransfers((prevTransfers: any) => {
      const updatedTransfers = [...prevTransfers];
      updatedTransfers[index] = {
        ...updatedTransfers[index],
        selectedAmountToTransfer: dollarsToCents(newAmount),
      };
      return updatedTransfers;
    });
  };

  const onWithholdFeeChange = (checked: boolean) => {
    setTransfers((prevTransfers: any) => {
      const updatedTransfers = [...prevTransfers];
      updatedTransfers[index] = {
        ...updatedTransfers[index],
        withholdClearFee: checked,
      };
      return updatedTransfers;
    });
  };

  // Strict-freeze: until the authoritative rail is known, do not render the
  // rail-specific transfer options (which party is hidden differs by rail) —
  // otherwise a Stripe order on a Finix-flipped account would briefly hide
  // legitimate transfer parties / auto-clear a valid selection.
  if (isProviderLoading) {
    return (
      <div className="flex flex-column">
        <LoadingSpinner />
      </div>
    );
  }

  // Error-fallback policy: this form moves real money, so on a failed rail
  // lookup we fail closed rather than guess from the account flag (which is
  // wrong for a Stripe-era order on a flipped account). The server guards both
  // rails too, so a wrong submit would 409 — this just avoids a confusing form.
  if (isProviderError) {
    return (
      <div className="flex flex-column">
        <p className="small-text">
          Couldn&apos;t determine this order&apos;s payment method. Please
          reopen this dialog to retry before transferring funds.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-column">
      <div className="flex justify-content-between">
        <p className="font-semibold mb-2">Transfer {index + 1}</p>
        {showRemoveButton ? (
          <StyledButton
            data-testid="remove-transfer"
            icon="pi pi-times-circle"
            className="p-button-text p-button-danger p-0 mb-0"
            style={{ alignSelf: "end" }}
            onClick={() => handleRemoveTransfer(index)}
          >
            <p className="small-text">Remove transfer</p>
          </StyledButton>
        ) : null}
      </div>
      <div className="flex flex-column">
        <Label htmlFor="from">From</Label>
        <Dropdown
          options={transferFromOptions}
          value={transfer.selectedPartyToTransferFrom}
          valueTemplate={valueTemplate}
          optionLabel="providerName"
          className="mt-2 mb-4"
          onChange={(e) => onTransferFromChange(e.value)}
          itemTemplate={itemTemplate}
          placeholder="Select party"
        />
        <Label htmlFor="to">To</Label>
        <Dropdown
          options={transferToOptions}
          value={transfer.selectedPartyToTransferTo}
          valueTemplate={valueTemplate}
          optionLabel="name"
          className="mt-2 mb-4"
          onChange={(e) => onTransferToChange(e.value)}
          placeholder="Select party"
          itemTemplate={itemTemplate}
          optionGroupLabel="label"
          optionGroupChildren="items"
          optionGroupTemplate={optionGroupTemplate}
        />
        <Label htmlFor="to">Amount</Label>
        <InputNumber
          value={centsToDollars(transfer.selectedAmountToTransfer)}
          max={
            amountMaxInCents != null
              ? centsToDollars(amountMaxInCents)
              : undefined
          }
          onChange={(e) => onAmountChange(e.value)}
          disabled={!transfer?.selectedPartyToTransferFrom}
          min={0}
          currency="USD"
          locale="en-US"
          mode="currency"
          inputId="amountToTransfer"
          className="mt-2 mb-4"
        />
        {showRefundCardPicker && (
          <>
            <Label htmlFor="refundCard">Refund to card</Label>
            <Dropdown
              inputId="refundCard"
              options={refundableCards.map((card) => {
                const takenByAnotherTransfer =
                  cardsChosenByOtherTransfers.includes(card.paymentId);
                // On Finix the card's own charges aren't the refund limit (the provider slice is), so
                // showing a card amount here is misleading — omit it and let the helper text below
                // state the real per-provider max. On Stripe the card's REMAINING refundable IS the
                // cap, so show that; fall back to the gross charged if the server couldn't read it.
                const remaining = getCardRemainingInCents(card);
                const cardAmountLabel = useFinix
                  ? ""
                  : ` — ${parsePrice(
                      centsToDollars(remaining ?? card.amountInCents)
                    )}`;
                return {
                  label: `${card.brand || "Card"}${
                    card.last4 ? ` •••• ${card.last4}` : ""
                  }${cardAmountLabel}${
                    takenByAnotherTransfer ? " (already selected above)" : ""
                  }`,
                  value: card.paymentId,
                  disabled: takenByAnotherTransfer,
                };
              })}
              optionDisabled="disabled"
              value={transfer.selectedRefundCardPaymentId}
              onChange={(e) => onRefundCardChange(e.value)}
              placeholder="Default (largest card first, may span both)"
              className="mt-2"
              showClear
            />
            {selectedRefundCard ? (
              <small className="mb-4 mt-1">
                {selectedCardCapInCents != null
                  ? `Up to ${parsePrice(
                      centsToDollars(selectedCardCapInCents)
                    )} can be refunded to this card${
                      usesProviderCardCap ? " from the selected provider" : ""
                    }.`
                  : "Select who to refund from above to see how much can be refunded to this card."}
              </small>
            ) : (
              <small className="mb-4 mt-1">
                No specific card chosen — refunds the largest card first, then
                the other card if the amount is larger.
              </small>
            )}
          </>
        )}
        {isSuperAdmin &&
          transfer.selectedPartyToTransferFrom?.id === null &&
          transfer.selectedPartyToTransferTo?.id !== null &&
          transfer.selectedPartyToTransferTo?.type === "provider" && (
            <div className="flex align-items-center mb-4">
              <Checkbox
                inputId={`withholdClearFee-${index}`}
                checked={transfer.withholdClearFee || false}
                onChange={(e) => onWithholdFeeChange(e.checked || false)}
              />
              <label htmlFor={`withholdClearFee-${index}`} className="ml-2">
                Withhold clear fee
              </label>
            </div>
          )}
      </div>
    </div>
  );
}
